import { useEffect, useState } from "react";
import React from "react";
import * as Yup from "yup";
import {
  Box,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  Typography,
  Switch,
  SelectChangeEvent,
  ListItemText,
  Checkbox,
  OutlinedInput,
  InputAdornment,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate, useParams } from "react-router-dom";
import { Category, ServiceDetails } from "../../types/General";
import { useLazyGetCategoryQuery } from "../../service/main";
import { useLazyGetServicesListQuery } from "../../service/services";
import {
  useAdddiscountMutation,
  useLazyGetDiscountDetailsQuery,
  useUpdatediscountMutation,
} from "../../service/discount";
import { useFormik } from "formik";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import Loader from "../../constants/Loader";
import CurrencyIcon from "../../components/dirham";
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const AddProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [services, setServices] = useState<ServiceDetails[]>([]);
  const [getServices] = useLazyGetServicesListQuery();
  const [discount, setDiscount] = useState<any>(null);
  const [getDiscountDetails, { isLoading: getDiscountDetailsLoading }] =
    useLazyGetDiscountDetailsQuery();

  const [addDiscount, { isLoading: isAdding }] = useAdddiscountMutation();
  const [updateDiscount, { isLoading: isUpdating }] =
    useUpdatediscountMutation();


  const fetchServices = async () => {
    try {
      const response = await getServices({ page: 1, limit: 1000 }).unwrap();
      if (response.statusCode === 200) {
        setServices(response?.data?.list || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getDiscountByIdApi = async () => {
    try {
      const response = await getDiscountDetails({ id }).unwrap();
      if (response.statusCode === 200) {
        const data = response?.data;
        setDiscount(data);
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to fetch discount details");
      console.error(error);
    }
  };

  const generateCode = () => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    const array = new Uint32Array(8);

    crypto.getRandomValues(array);

    for (let i = 0; i < 8; i++) {
      result += characters[array[i] % characters.length];
    }

    return result;
  };
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Offer/Discount name is required")
      .min(3, "Name must be at least 3 characters")
      .max(20, "Name must not exceed 20 characters")
      .matches(/^\S/, "Name cannot start with a space"),
    discountType: Yup.number()
      .oneOf([1, 2], "Discount type must be 1 (percentage) or 2 (flat)")
      .required("Discount type is required"),
    discount: Yup.number()
      .required("Discount value is required")
      .min(1, "Discount must be at least 1")
      .when("discountType", {
        is: 1,
        then: (schema) =>
          schema.max(100, "Percentage discount cannot exceed 100"),
        otherwise: (schema) =>
          schema.max(9999999, "Flat discount cannot exceed 7 digits"),
      }),
    validFrom: Yup.string()
      .required("Valid from date is required")
      .test(
        "is-not-past",
        "Valid from date cannot be in the past",
        (value) => {
          if (!value) return true;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return new Date(value) >= today;
        }
      ),
    validTo: Yup.string()
      .required("Valid to date is required")
      .test(
        "is-after",
        "Valid to date must be on or after valid from date",
        (value, context) => {
          const { validFrom } = context.parent;
          if (!validFrom || !value) return true;
          return new Date(value) >= new Date(validFrom);
        },
      ),
    serviceIds: Yup.array().of(Yup.string()).default([]),
  }).test(
    "at-least-one-selection",
    "Please select at least one service",
    (value, context) => {
      const { serviceIds } = value;
      if (!serviceIds?.length) {
        return context.createError({
          path: "serviceIds",
          message: "Please select at least one service",
        });
      }
      return true;
    },
  );
  const formik = useFormik({
    initialValues: {
      name: discount?.name || "",
      discountType: discount?.discountType ? Number(discount.discountType) : 1,
      discount: discount?.discount || "",
      validFrom: discount?.validFrom || "",
      validTo: discount?.validTo || "",
      serviceIds: discount?.services?.map((svc: any) => svc.id) || [],
      code: discount?.code || "",
    },
    enableReinitialize: true,

    validationSchema,
    onSubmit: async (values) => {
      try {
        const body: any = {
          name: values.name,
          discountType: values.discountType,
          discount: values.discount,
          validFrom: values.validFrom,
          validTo: values.validTo,
          serviceIds: values.serviceIds,
          code: values.code,
        };

        let response;
        if (id) {
          response = await updateDiscount({ id: id || "", body }).unwrap();
        } else {
          body.code = generateCode();
          body.isActive = true;
          response = await addDiscount(body).unwrap();
        }

        if (response.statusCode === 200 || response.statusCode === 201) {
          toast.success(
            id
              ? "Discount updated successfully"
              : "Discount added successfully",
          );
          navigate("/discount-coupon");
        } else {
          toast.error(response.message || "Something went wrong");
        }
      } catch (error: any) {
        toast.error(error?.data?.message || "An error occurred");
        console.error(error);
      }
    },
  });
  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (id) {
      getDiscountByIdApi();
    }
  }, [id]);



  return (
    <>
      <Loader isLoad={isAdding || isUpdating || getDiscountDetailsLoading} />

      <div className="main_title">
        <h1>{id ? "Edit" : "Add"} Discount and Coupon</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          <button onClick={() => navigate("/discount-coupon")}>Discount & Coupon Management</button> - {id ? "Edit" : "Add"} Discount & Coupon Management
        </p>
      </div>
      <div className="cards custom_form_details">
        <div className="form">
          <div className="gap_m w_100">
            <div className="control_group w_50">
              <label htmlFor="name">Offer/Discount Name *</label>
              <TextField
                id="name"
                name="name"
                placeholder="Enter Discount Name"
                fullWidth
                hiddenLabel
                value={formik.values.name}
                inputProps={{ maxLength: 20 }}
                onChange={(e) => {
                  if (e.target.value.startsWith(" ")) return;
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.name && !!formik.errors.name}
                helperText={
                  formik.touched.name && (formik.errors.name as string)
                }
                FormHelperTextProps={{
                  className: "err_txt",
                }}
              />
            </div>

            <div className="control_group w_50">
              <label htmlFor="discountType">Discount Type *</label>
              <Select
                fullWidth
                name="discountType"
                value={formik.values.discountType}
                onChange={(e) => {
                  formik.handleChange(e);
                  formik.setFieldValue("discount", "");
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.discountType && !!formik.errors.discountType
                }
              >
                <MenuItem value={1}>Percentage</MenuItem>
                <MenuItem value={2}>Flat</MenuItem>
              </Select>
              {formik.touched.discountType && formik.errors.discountType && (
                <p className="err_txt">
                  {formik.errors.discountType as string}
                </p>
              )}
            </div>

            <div className="control_group w_50">
              <label htmlFor="discount">
                {formik.values.discountType === 1
                  ? "Discount (%)"
                  : "Flat Amount"}{" "}
                *
              </label>
              <TextField
                id="discount"
                name="discount"
                type="number"
                placeholder={
                  formik.values.discountType === 1
                    ? "Enter Discount (%)"
                    : "Enter Flat Amount"
                }
                fullWidth
                hiddenLabel
                value={formik.values.discount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    formik.handleChange(e);
                    return;
                  }
                  if (val === "0") return;
                  if (Number(val) < 0) return;
                  if (formik.values.discountType === 1 && Number(val) > 100) return;
                  if (formik.values.discountType === 2 && Number(val) > 9999999) return;

                  if (!Number.isNaN(Number(val))) {
                    if (val.includes('.') && val.split('.')[1].length > 2) return;
                    formik.handleChange(e);
                  }
                }}
                onKeyDown={(e) => {
                  if (["-", "+", "e", "E"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.discount && !!formik.errors.discount}
                helperText={
                  formik.touched.discount && (formik.errors.discount as string)
                }
                FormHelperTextProps={{
                  className: "err_txt",
                }}
                inputProps={{
                  min: 0,
                  ...(formik.values.discountType === 1 && { max: 99 }),
                  ...(formik.values.discountType != 1 && { max: 9999999 }),
                }}
                InputProps={{
                  startAdornment: formik.values.discountType === 2 ? (
                    <InputAdornment position="start"><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} /></InputAdornment>
                  ) : null,
                }}
              />
            </div>
            <div className="control_group w_50">
              <label htmlFor="validFrom">Valid From *</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  className="form_control"
                  disablePast

                  value={
                    formik.values.validFrom
                      ? dayjs(formik.values.validFrom)
                      : null
                  }
                  onChange={(date) => {
                    formik.setFieldValue(
                      "validFrom",
                      date ? date.startOf("day").toISOString() : "",
                    );
                  }}
                  slots={{
                    openPickerIcon: () => (
                      <img src="/static/images/date_icon.svg" alt="Pick date" />
                    ),
                  }}
                  slotProps={{
                    textField: {
                      id: "validFrom",
                      placeholder: "Select Date",
                      fullWidth: true,
                      hiddenLabel: true,
                      error:
                        formik.touched.validFrom && !!formik.errors.validFrom,
                      helperText:
                        formik.touched.validFrom && formik.errors.validFrom
                          ? String(formik.errors.validFrom)
                          : "",
                      FormHelperTextProps: {
                        className: "err_txt",
                      },
                    },
                  }}
                  desktopModeMediaQuery="(min-width:0px)"
                />
              </LocalizationProvider>
            </div>
            <div className="control_group w_50">
              <label htmlFor="validTo">Valid To *</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  className="form_control"
                  disablePast
                  minDate={
                    formik.values.validFrom
                      ? dayjs(formik.values.validFrom)
                      : dayjs()
                  }
                  value={
                    formik.values.validTo ? dayjs(formik.values.validTo) : null
                  }
                  onChange={(date) => {
                    formik.setFieldValue(
                      "validTo",
                      date ? date.startOf("day").toISOString() : "",
                    );
                  }}
                  slots={{
                    openPickerIcon: () => (
                      <img src="/static/images/date_icon.svg" alt="Pick date" />
                    ),
                  }}
                  slotProps={{
                    textField: {
                      id: "validTo",
                      placeholder: "Select Date",
                      fullWidth: true,
                      hiddenLabel: true,
                      error: formik.touched.validTo && !!formik.errors.validTo,
                      helperText:
                        formik.touched.validTo && formik.errors.validTo
                          ? String(formik.errors.validTo)
                          : "",
                      FormHelperTextProps: {
                        className: "err_txt",
                      },
                    },
                  }}
                  desktopModeMediaQuery="(min-width:0px)"
                />
              </LocalizationProvider>
            </div>


            <div className="control_group w_50">
              <label htmlFor="services">Applicable Services*</label>
              <Select
                labelId="Applicable Services"
                id="serviceIds"
                name="serviceIds"
                multiple
                displayEmpty
                value={formik.values.serviceIds}
                onChange={(e) =>
                  formik.setFieldValue("serviceIds", e.target.value)
                }
                input={<OutlinedInput />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span style={{ color: "#aaa" }}>Select Services</span>
                    );
                  }
                  return (
                    services
                      ?.filter((svc) => selected.includes(svc.id))
                      .map((svc) => svc?.name)
                      .join(", ") || ""
                  );
                }}
                MenuProps={MenuProps}
              >
                {services?.length > 0 ? (
                  services.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      <Checkbox
                        checked={formik.values.serviceIds.includes(service.id)}
                      />
                      <ListItemText primary={service?.name} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <ListItemText primary="No services found" />
                  </MenuItem>
                )}
              </Select>
              {(formik.touched.serviceIds || formik.submitCount > 0) && formik.errors.serviceIds && (
                <p className="err_txt">
                  {formik.errors.serviceIds as string}
                </p>
              )}
            </div>

            {id && (
              <div className="control_group w_50">
                <label htmlFor="code">Coupon Code</label>
                <TextField
                  id="code"
                  name="code"
                  fullWidth
                  hiddenLabel
                  value={formik.values.code}
                  disabled
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Box mt={3}>
        <Button
          className="btn btn_primary"
          onClick={() => formik.handleSubmit()}
          disabled={isAdding || isUpdating}
        >
          {id ? "Update Discount" : "Add Discount"}
        </Button>
      </Box>
    </>
  );
};

export default AddProduct;
